import { RunStatus } from "@kaggle/components";
import { Kaggle } from "@kaggle/sdk";
import { getServices } from "../../../api/getServices";
import BatchedRunStatus from "../../types/BatchedRunStatus";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { BatchedRunActionKey } from "./ActionKeys";
import { setRunStatus } from "./runs";
import { appendTerminalError } from "./terminal";

import KernelVersionType = Kaggle.Sdk.Kernels.KernelVersionType;

/** This operation should set sequence, run, and status atomically. */
interface SetBatchedRunAction
  extends AppAction<
    BatchedRunActionKey.SET_BATCHED_RUN,
    {
      run: number;
      sequence: number;
      versionType: KernelVersionType.BATCH | KernelVersionType.QUICK;
    }
  > {}
interface RemoveBatchedRunAction
  extends AppAction<BatchedRunActionKey.REMOVE_BATCHED_RUN> {}
interface SetBatchedRunStatusAction
  extends AppAction<
    BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
    { status: BatchedRunStatus; runId?: number }
  > {}
interface SetShowBatchedRunProgressSnackbarAction
  extends AppAction<
    BatchedRunActionKey.SET_SHOW_BATCHED_RUN_PROGRESS_SNACKBAR,
    { show: boolean }
  > {}
export interface BatchedRunUploadingAction
  extends AppAction<
    BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
    { status: BatchedRunStatus.UPLOADING; progress: number | null }
  > {}
interface SetBatchedRunErrorMessage
  extends AppAction<
    BatchedRunActionKey.SET_BATCHED_RUN_ERROR_MESSAGE,
    { message: string | null }
  > {}

export type BatchedRunAction =
  | SetBatchedRunAction
  | SetBatchedRunStatusAction
  | BatchedRunUploadingAction
  | SetShowBatchedRunProgressSnackbarAction
  | RemoveBatchedRunAction
  | SetBatchedRunErrorMessage;

export const putBatchedRun = (
  kernelRunId: number,
  sequence: number,
  versionType: KernelVersionType.BATCH | KernelVersionType.QUICK
): SetBatchedRunAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_BATCHED_RUN,
    payload: { run: kernelRunId, sequence, versionType },
  });

export const setBatchedRunCommitting = (
  runId?: number
): SetBatchedRunStatusAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
    payload: { status: BatchedRunStatus.COMMITTING, runId },
  });

export const setBatchedRunCancelling = (): SetBatchedRunStatusAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
    payload: { status: BatchedRunStatus.CANCELLING },
  });

export const setBatchedRunCancelled = (): SetBatchedRunStatusAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
    payload: { status: BatchedRunStatus.CANCELLED },
  });

export const setBatchedRunUploading = (
  progress: number
): BatchedRunUploadingAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
    payload: { status: BatchedRunStatus.UPLOADING, progress },
  });

export const setBatchedRunUploadingFiles = (): SetBatchedRunStatusAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
    payload: { status: BatchedRunStatus.UPLOADING_FILES },
  });

export const showBatchedRunProgressSnackbar = (
  show: boolean
): SetShowBatchedRunProgressSnackbarAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_SHOW_BATCHED_RUN_PROGRESS_SNACKBAR,
    payload: { show },
  });

export const removeBatchedRun = (): RemoveBatchedRunAction =>
  getStore().dispatch({
    type: BatchedRunActionKey.REMOVE_BATCHED_RUN,
    payload: {},
  });

export const setBatchedRunErrorMessage = (
  message: string
): SetBatchedRunErrorMessage =>
  getStore().dispatch({
    type: BatchedRunActionKey.SET_BATCHED_RUN_ERROR_MESSAGE,
    payload: { message },
  });

export const cancelCommit = async (runId: number | null) => {
  const services = getServices();
  const state = getStore().getState();
  const kernelRunId = runId || state.batchedRun.id;
  if (kernelRunId === null) {
    return;
  }

  setRunStatus(kernelRunId, RunStatus.CANCEL_REQUESTED);
  setBatchedRunCancelling();
  try {
    await services.kernelsClient.kernelsService.cancelKernelSession({
      kernelSessionId: kernelRunId,
    });
  } catch (error) {
    appendTerminalError("An error occurred cancelling commit.", error);
    throw error;
  }

  setRunStatus(kernelRunId, RunStatus.CANCEL_ACKNOWLEDGED);
  setBatchedRunCancelled();

  // Wait 8 seconds to remove the batched run completion message. This
  // gives the user time to access the viewer link.
  window.setTimeout(() => {
    showBatchedRunProgressSnackbar(false);
    removeBatchedRun();
  }, 8000);
};
